import { Injectable } from '@angular/core';
import { StepperContextService, StepperMode } from './stepper-context.service';
import { StepperAnswersService } from './stepper-answers.service';
import { StepperTasksService } from './stepper-tasks.service';
import { GettingStartedSteps } from '../enums/getting-started-steps.enum';
import { AboutYouSteps } from '../enums/about-you-steps.enum';
import { PersonalInfoInputs } from '../components/sections/client-initiated-loan/about-you/steps/borrower-personal-info/borrower-personal-info.component';
import { SubmitSteps } from '../enums/submit-steps.enum';
import { ConfirmationInputs } from '../components/sections/client-initiated-loan/submit/steps/confirmation/confirmation.component';
import { MilitaryOptions } from '../components/sections/client-initiated-loan/about-you/steps/borrower-military/borrower-military.component';
import { CurrentAddressInputs } from '../components/sections/client-initiated-loan/about-you/steps/borrower-current-address/borrower-current-address.component';
import { PropertySteps } from '../enums/property-steps.enum';
import { first } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment as env } from '../../../../environments/environment';
import { LgvLoanApplicationService } from '../../../core/services/lgv-loan-application.service';
import { Citizenship, IncomeTypes } from '../enums/loan-info-mapping.enum';
import { IncomeAndAssetsSteps } from '../enums/income-and-assets-steps.enum';

@Injectable({
    providedIn: 'root'
})
export class StepperLoanAppService {
    isPurchase =
        this.answerService.getAnswer(GettingStartedSteps.LoanPurpose) ===
        'Purchase'
            ? true
            : false;
    taskItems: any[] = [];
    customFields: any[] = [];

    constructor(
        public contextService: StepperContextService,
        public answerService: StepperAnswersService,
        public taskService: StepperTasksService,
        private http: HttpClient,
        public lgvLoanAppSvc: LgvLoanApplicationService
    ) {
        this.lgvLoanAppSvc.taskItemsPublic$.pipe().subscribe(x => {
            this.taskItems = x;
        });
    }

    createLoanApp() {
        let loanInfo = this.createLoanInfo();

        this.taskService.addTask('Loan Application (1003)');

        const filteredTaskItems = this.taskItems.filter(task =>
            this.taskService.getTasks().includes(task.name)
        );

        // Convert TaskItems to proper Task structure for the API
        const tasks = filteredTaskItems.map(taskItem => {
            // Clone the taskItem to avoid mutating the original
            let taskItemClone = structuredClone(taskItem);

            // Create a proper Task object
            let appTask = {
                id: null, // Task ID should be null for new ApplicationTasks
                name: taskItemClone.name,
                description: taskItemClone.description ?? taskItemClone.name,
                item: null, // Will be set below
                displayOrder: null,
                type: taskItemClone.type
            };

            // Remove type from taskItemClone before assigning to item
            // appTask.item can not have a type or the API will 500
            if (taskItemClone.type) {
                delete taskItemClone.type;
            }
            appTask.item = taskItemClone; // TaskItem (with its ID) becomes the item

            return appTask;
        });

        let appModel = {
            clientExternalId: this.contextService.clientExternalId,
            loanInfo,
            tasks: tasks
        };

        return this.contextService.mode === StepperMode.ClientMode
            ? this.startClientLoanApplication(appModel)
            : this.startLoanApplication(this.contextService.agentId, appModel);
    }

    createLoanInfo() {
        const subjectProperty = this.getSubjectProperty();
        const isClient = this.contextService.mode == StepperMode.ClientMode;

        let loanInfo = {
            assignments: {
                loanOfficer: {
                    id: isClient ? this.contextService.agentId : ''
                }
            },
            borrowers: [
                {
                    clientId: isClient
                        ? this.contextService.clientInfo?.id
                        : '',
                    isBeta: this.contextService.isBeta,
                    firstName: this.answerService.getAnswer(
                        AboutYouSteps.BorrowerPersonalInfo
                    )[PersonalInfoInputs.FirstName],
                    lastName: this.answerService.getAnswer(
                        AboutYouSteps.BorrowerPersonalInfo
                    )[PersonalInfoInputs.LastName],
                    contacts: {
                        email: this.answerService.getAnswer(
                            AboutYouSteps.BorrowerPersonalInfo
                        )[PersonalInfoInputs.Email],
                        mobilePhone: this.answerService.getAnswer(
                            SubmitSteps.Confirmation
                        )[ConfirmationInputs.Phone]
                    },
                    veteranDetail: {
                        isVeteran:
                            this.answerService.getAnswer(
                                AboutYouSteps.BorrowerMilitary
                            ) === MilitaryOptions.Yes
                    },
                    citizenship: {
                        name:
                            Citizenship[
                                this.answerService.getAnswer(
                                    AboutYouSteps.BorrowerCitizenship
                                )
                            ]
                    },
                    currentAddress: {
                        street: this.answerService.getAnswer(
                            AboutYouSteps.BorrowerCurrentAddress
                        )[CurrentAddressInputs.Address1],
                        city: this.answerService.getAnswer(
                            AboutYouSteps.BorrowerCurrentAddress
                        )[CurrentAddressInputs.City],
                        state: this.answerService.getAnswer(
                            AboutYouSteps.BorrowerCurrentAddress
                        )[CurrentAddressInputs.State],
                        zipCode: this.answerService.getAnswer(
                            AboutYouSteps.BorrowerCurrentAddress
                        )[CurrentAddressInputs.Zip]
                    }
                }
            ],
            purpose: {
                name: this.answerService.getAnswer(
                    GettingStartedSteps.LoanPurpose
                )
            },
            occupancy: {
                name: this.answerService
                    .getAnswer(PropertySteps.PropertyUsage)
                    .replaceAll(' ', '')
            },
            subjectPropertyAddress: {
                city: subjectProperty.city ?? '',
                state: subjectProperty.state ?? '',
                zipCode: subjectProperty.zip ?? ''
            }
        };
        return loanInfo;
    }

    getSubjectProperty(): any {
        let address = {
            city: '',
            state: '',
            zip: ''
        };
        if (this.isPurchase) {
            address.city = this.answerService.getAnswer(
                PropertySteps.PurchaseSubjectAddress
            )['City'];
            address.state = this.answerService.getAnswer(
                PropertySteps.PurchaseSubjectAddress
            )['State'];
            address.zip = this.answerService.getAnswer(
                PropertySteps.PurchaseSubjectAddress
            )['Zip'];
        } else {
            address.city = this.answerService.getAnswer(
                PropertySteps.RefinanceSubjectAddress
            )['City'];
            address.state = this.answerService.getAnswer(
                PropertySteps.RefinanceSubjectAddress
            )['State'];
            address.zip = this.answerService.getAnswer(
                PropertySteps.RefinanceSubjectAddress
            )['Zip'];
        }
        return address;
    }

    public startLoanApplication(agentId: string, model: any) {
        const url = `${env.LgvApiBaseUrl}/public/users/${agentId}/loanapplications`;
        return this.http.post<any>(url, model).pipe(first());
    }

    public startClientLoanApplication(model: any) {
        const url = `${env.LgvApiBaseUrl}/loanapplications?clientId=${this.contextService.clientInfo.id}`;
        return this.http.post<any>(url, model).pipe(first());
    }
}
